/**
 * Browser-side image compression — resizes images to max edge of 1600px and
 * re-encodes as JPEG 0.85 quality. Returns a File ready for upload.
 *
 * Drastically reduces mobile photo uploads (10 MB → ~400 KB typical) and
 * avoids ingress timeouts / "Es ist ein Fehler aufgetreten" generic errors.
 */
export async function compressImage(file, { maxEdge = 1600, quality = 0.85 } = {}) {
    if (!file || !file.type.startsWith("image/")) return file;

    // Read into bitmap
    const bitmap = await new Promise((resolve, reject) => {
        const url = URL.createObjectURL(file);
        const img = new Image();
        img.onload = () => {
            URL.revokeObjectURL(url);
            resolve(img);
        };
        img.onerror = (e) => {
            URL.revokeObjectURL(url);
            reject(e);
        };
        img.src = url;
    });

    const { width, height } = bitmap;
    const longest = Math.max(width, height);
    const scale = longest > maxEdge ? maxEdge / longest : 1;
    const w = Math.round(width * scale);
    const h = Math.round(height * scale);

    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(bitmap, 0, 0, w, h);

    const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", quality));
    if (!blob) return file;

    const newName = file.name.replace(/\.[^.]+$/, "") + ".jpg";
    return new File([blob], newName, { type: "image/jpeg", lastModified: Date.now() });
}
